import { AreaComumForm } from "core/features/area-comum/typings/index";
import { Box, Flex } from "@chakra-ui/layout";
import { useDisclosure } from "@chakra-ui/react";
import useHorariosAreaComum from "core/features/area-comum/hooks/useHorariosAreaComum";
import {
  AreaComumHorario,
  AreaComumDiaStatus,
} from "core/features/area-comum/typings";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { ReservaForm } from "core/features/reservas/typings";
import { useLoading } from "components/GlobalLoading";
import moment from "moment";
import { useMemo, useState } from "react";
import { Control, UseFormSetValue } from "react-hook-form";
import Field from "ui/components/Input/Field";
import Stepper from "ui/components/Stepper";
import FormAreaComum from "./FormAreaComum";
import FormCalendario from "./FormCalendario";
import FormHorario from "./FormHorario";
import FormPersonalizar from "./FormPersonalizar";
import CollapseTaxas from "./CollapseTaxas";
import { groupValuesByDateAndType } from "../util/helper";
import colors from "resources/theme/colors";
import ModalTermos from "../ModalTermos";

function FormReserva({
  control,
  form,
  setValue,
  currentStep,
  setCurrentStep,
  areaComum,
  isFetchingAreaComum,
  onNext,
}: {
  control: Control<ReservaForm, object>;
  currentStep: number;
  setValue: UseFormSetValue<ReservaForm>;
  setCurrentStep: any;
  form: ReservaForm;
  areaComum?: AreaComumForm;
  isFetchingAreaComum?: boolean;
  onNext?: () => any;
}) {
  const {
    isOpen: isOpenModalTermos,
    onOpen: onOpenModalTermos,
    onClose: onCloseModalTermos,
  } = useDisclosure();

  const { tenant } = useCurrentTenant();

  const [dataInicio, setDataInicio] = useState<string>(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [dataFim, setDataFim] = useState<string>(
    moment().endOf("month").format("YYYY-MM-DD")
  );

  const _steps = [
    { title: "Escolher espaço" },
    { title: "Personalizar evento" },
    { title: "Escolher data e horário" },
  ];

  const { data: datas, isFetching } = useHorariosAreaComum({
    tenantId: tenant.id,
    id: form.areaComumId,
    dataInicio,
    dataFim,
    unidadeId: form.unidadeId,
  });
  useLoading(isFetching || !!isFetchingAreaComum);

  const currentData: AreaComumHorario | null = useMemo(() => {
    if (!datas) {
      return null;
    }

    const item = datas.find((item) => item.data === form.data);

    return item ?? null;
  }, [datas, form.data]);

  const disabledDates = getDisabledDates(dataInicio, dataFim, datas);

  const taxas = groupValuesByDateAndType({ valores: areaComum?.valores || [] });

  const onChangeDataInicio = (data: string) => {
    setValue("data", data);

    const _currentData = datas?.find((item) => item.data === data);

    if (
      _currentData &&
      _currentData.status === AreaComumDiaStatus.Livre &&
      _currentData.horarios.length === 1 &&
      _currentData.horarios[0].status === AreaComumDiaStatus.Livre
    ) {
      setValue("horaInicio", _currentData?.horarios[0].inicio);
      setValue("horaFim", _currentData.horarios[0].fim);
    } else if (
      _currentData &&
      (_currentData.status !== AreaComumDiaStatus.Livre ||
        _currentData.horarios.length !== 1)
    ) {
      setValue("horaInicio", "");
      setValue("horaFim", "");
    }
  };

  return (
    <>
      <Box h="16px" />
      <Stepper
        steps={_steps}
        current={currentStep}
        onClickStep={setCurrentStep}
      />

      <Box py="32px">
        <Box display={currentStep === 0 ? "block" : "none"}>
          <Field
            control={control}
            name="areaComumId"
            rules={{ required: true }}
            render={({ field }) => <FormAreaComum {...field} />}
          />
        </Box>

        <Box display={currentStep === 1 ? "block" : "none"}>
          <FormPersonalizar
            control={control}
            form={form}
            onOpenTermos={onOpenModalTermos}
            setValue={setValue}
          />
        </Box>

        {currentStep === 2 ? (
          <Box>
            <Field
              control={control}
              name="data"
              render={({ field }) => (
                <FormCalendario
                  setDataInicio={setDataInicio}
                  setDataFim={setDataFim}
                  disabledDates={disabledDates}
                  datas={datas}
                  dataInicio={dataInicio}
                  dataFim={dataFim}
                  value={field.value}
                  onChange={onChangeDataInicio}
                />
              )}
            />

            {taxas.length ? <CollapseTaxas taxas={taxas} /> : null}

            <Flex gap="32px">
              <Flex>
                <Flex
                  mr="8px"
                  w="10px"
                  h="10px"
                  mt="6px"
                  rounded="full"
                  bg={colors.yellow500}
                />
                <Flex>Restam alguns horários disponíveis</Flex>
              </Flex>

              <Flex>
                <Flex
                  mr="8px"
                  w="10px"
                  h="10px"
                  mt="6px"
                  rounded="full"
                  bg="#E53E3E"
                />
                <Flex>Ocupado</Flex>
              </Flex>

              <Flex>
                <Flex
                  mr="8px"
                  w="10px"
                  h="10px"
                  mt="6px"
                  rounded="full"
                  bg={colors.cyan}
                />
                <Flex>Bloqueado pela administração</Flex>
              </Flex>
            </Flex>

            {form.data && currentData ? (
              <Field
                control={control}
                name="horaInicio"
                render={({ field }) => (
                  <FormHorario
                    dia={currentData.data}
                    horarios={currentData.horarios}
                    value={field.value}
                    onChange={(inicio, fim) => {
                      field.onChange(inicio);
                      setValue("horaFim", fim);
                    }}
                  />
                )}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>

      <ModalTermos
        isOpen={isOpenModalTermos}
        onClose={onCloseModalTermos}
        termos={areaComum?.termoUso || ""}
      />
    </>
  );
}

const getDisabledDates = (
  start: string,
  end: string,
  available?: AreaComumHorario[]
) => {
  let disabledDates: string[] = [];

  if (!available) return [];

  for (
    let _start = start;
    moment(_start).subtract(1, "day").format("YYYY-MM-DD") !== end;
    _start = moment(_start).add(1, "day").format("YYYY-MM-DD")
  ) {
    const apiDate = available.find((d) => d.data === _start);

    if (
      !apiDate ||
      moment(_start).isBefore(moment().format("YYYY-MM-DD"), "day")
    ) {
      disabledDates.push(_start);
    }
  }
  return disabledDates;
};

export default FormReserva;
